import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  BookOpen,
  ChevronRight,
  DollarSign,
  Eye,
  Info,
  Package2,
  Printer,
  Users,
} from "lucide-react";
import { FC } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { LoadingScreen } from "../components/common/LoadingScreen";
import { Button } from "../components/ui/Button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/Tooltip";
import { fetchWithAuth } from "../hooks/useAuth";
import { formatPrice } from "../utils/format";

interface DashboardStats {
  totalOrders: number;
  totalProducts: number;
  totalCustomers: number;
  totalRevenue: number;
  businessMetrics: {
    performance: {
      growth: {
        orders: {
          current: number | null;
          previous: number | null;
          growth: number | null;
        };
        revenue: {
          current: number | null;
          previous: number | null;
          growth: number | null;
        };
      };
      orderCompletion: Record<string, number>;
      monthlyRevenue: Record<string, number>;
      customerRetention: {
        repeatCustomers: number | null;
        retentionRate: number | null;
      };
      popularProducts: Array<{
        bookId: number;
        totalQuantity: number;
      }>;
    };
    orders: {
      byStatus: Array<{ status: string; _count: number }>;
      trend: Array<{ createdAt: string; _count: number }>;
      averageValue: number;
    };
    payments: {
      byMethod: Array<{
        method: string;
        _count: number;
        _sum: { amount: number | null };
      }>;
      successRate: Array<any>;
      refunds: {
        count: number;
        amount: number;
      };
    };
    shipping: {
      performance: Array<{
        judet: string | null;
        shippingStatus: string | null;
        _count: number;
      }>;
      regionalAnalytics: {
        successRate: Record<string, { total: number; success: number }>;
        averageDeliveryTime: Record<string, number>;
        failureAnalysis: Record<string, number>;
      };
    };
    products: {
      bestSellers: Array<{
        bookId: number;
        totalQuantity: number;
        totalRevenue: number;
      }>;
      stockTurnover: Array<{
        id: number;
        name: string;
        stock: number;
        monthlySales: number;
        turnoverRate: number;
      }>;
    };
  };
  recentOrders: Array<{
    id: number;
    customerName: string;
    customerEmail: string;
    totalPrice: number;
    status: string;
    createdAt: string;
    awbNumber: string | null;
    metadata?: { awbPrinted?: boolean };
    items: Array<{
      bookName: string;
      quantity: number;
      price: number;
    }>;
  }>;
  lowStockProducts: Array<{
    id: number;
    name: string;
    stock: number;
    price: number;
    outOfStock: boolean;
  }>;
}

import { Card } from "@/components/ui/Card";
import { samedayStatusConfig } from "../config/sameday.status.config";

const getStatusColor = (status: string): string => {
  // First try to map numeric status IDs
  const numericStatus = parseInt(status);
  if (
    !isNaN(numericStatus) &&
    samedayStatusConfig.statusMapping[numericStatus]
  ) {
    const mappedStatus = samedayStatusConfig.statusMapping[numericStatus];
    switch (mappedStatus) {
      case "DELIVERED":
        return "bg-green-100 text-green-800";
      case "IN_TRANSIT":
        return "bg-yellow-100 text-yellow-800";
      case "FAILED":
        return "bg-red-100 text-red-800";
      case "RETURNED":
        return "bg-purple-100 text-purple-800";
      case "CANCELED":
        return "bg-red-100 text-red-800";
      case "REGISTERED":
        return "bg-blue-100 text-blue-800";
    }
  }

  // Fallback to string status mapping
  switch (status) {
    case "PENDING":
      return "bg-yellow-100 text-yellow-800";
    case "Anulată":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const getStatusLabel = (status: string): string => {
  // First try to map numeric status IDs
  const numericStatus = parseInt(status);
  if (
    !isNaN(numericStatus) &&
    samedayStatusConfig.statusLabels[numericStatus]
  ) {
    return samedayStatusConfig.statusLabels[numericStatus];
  }

  // Fallback to string status mapping
  switch (status) {
    case "PENDING":
      return "În așteptare";
    case "Anulată":
      return "Anulată";
    default:
      return status;
  }
};

export const DashboardPage: FC = () => {
  const queryClient = useQueryClient();
  const { data: stats, isLoading } = useQuery<DashboardStats>({
    queryKey: ["dashboard-stats"],
    refetchInterval: 120000, // 2 minutes
    queryFn: async () => {
      const response = await fetchWithAuth("/api/admin/dashboard/stats");
      if (!response.ok) throw new Error("Eroare la încărcarea statisticilor");
      return response.json();
    },
  });

  console.log("stats", stats?.recentOrders);

  const handlePrintAwb = async (orderId: number) => {
    // Prevent printing for cancelled orders or missing AWB
    const orderFromList = stats?.recentOrders?.find((o) => o.id === orderId);
    if (!orderFromList) {
      toast.error("Comanda nu a fost găsită");
      return;
    }
    if (orderFromList.status === "Anulată") {
      toast.error("Comanda este anulată și nu poate fi printată");
      return;
    }
    if (!orderFromList.awbNumber) {
      toast.error("AWB indisponibil pentru această comandă");
      return;
    }
    try {
      const response = await fetchWithAuth(
        `/api/livrare/documents/${orderId}/download/awb`,
        { method: "GET" }
      );
      if (!response.ok) throw new Error("Eroare la descărcarea AWB");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      let printFrame: HTMLIFrameElement | null = null;

      try {
        const markResponse = await fetchWithAuth(
          `/api/admin/orders/${orderId}/mark-awb-printed`,
          { method: "POST" }
        );
        if (!markResponse.ok) {
          throw new Error("Eroare la actualizarea statusului AWB");
        }

        // Refresh dashboard stats after marking printed
        queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });

        const printWindow = window.open(url, "_blank");
        if (printWindow) {
          printWindow.onload = () => {
            try {
              printWindow.print();
              const checkPrintDialog = setInterval(() => {
                if (!document.hasFocus()) return;
                clearInterval(checkPrintDialog);
                printWindow.close();
              }, 1000);
            } catch (error) {
              printWindow.close();
            }
          };
        } else {
          // Fallback to iframe if popup blocked
          printFrame = document.createElement("iframe");
          printFrame.style.cssText =
            "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
          printFrame.src = url;
          document.body.appendChild(printFrame);

          setTimeout(() => {
            try {
              if (printFrame?.contentWindow) {
                printFrame.contentWindow.print();
              }
            } catch (error) {
            } finally {
              setTimeout(() => {
                if (printFrame && document.body.contains(printFrame)) {
                  document.body.removeChild(printFrame);
                }
              }, 1000);
            }
          }, 1000);
        }

        toast.success("AWB printat cu succes");
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "Eroare la printarea AWB"
        );
      } finally {
        try {
          if (printFrame && document.body.contains(printFrame)) {
            document.body.removeChild(printFrame);
          }
          window.URL.revokeObjectURL(url);
        } catch (error) {}
      }
    } catch (error) {
      toast.error("Eroare la printarea AWB");
    }
  };

  // removed per-row fetch; rely on recentOrders metadata

  console.log("stats", stats);

  if (isLoading) {
    return <LoadingScreen fullScreen />;
  }

  return (
    <div className="p-6 space-y-8 bg-background dark:bg-background  min-h-screen">
      {/* Performance Overview */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {/* Orders stat */}
        <Card className=" ">
          <div className="p-6">
            <div className="flex items-center gap-3">
              <Package2 className="w-8 h-8 text-primary" />
              <div>
                <div className="text-sm font-medium text-foreground">
                  Total Comenzi
                </div>
                <div className="mt-1 text-3xl font-semibold text-foreground">
                  {stats?.totalOrders ?? 0} comenzi
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Revenue stat */}
        <Card className=" ">
          <div className="p-6">
            <div className="flex items-center gap-3">
              <DollarSign className="w-8 h-8 text-primary" />
              <div>
                <div className="text-sm font-medium text-foreground">
                  Total Venituri (comenzi livrate)
                </div>
                <div className="mt-1 text-3xl font-semibold text-foreground">
                  {formatPrice(stats?.totalRevenue ?? 0)}
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Customers stat */}
        <Card className=" ">
          <div className="p-6">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-primary" />
              <div>
                <div className="text-sm font-medium text-foreground">
                  Total Clienți
                </div>
                <div className="mt-1 text-3xl font-semibold text-foreground">
                  {stats?.totalCustomers ?? 0} clienți
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Books stat */}
        <Card className=" ">
          <div className="p-6">
            <div className="flex items-center gap-3">
              <BookOpen className="w-8 h-8 text-primary" />
              <div>
                <div className="text-sm font-medium text-foreground">
                  Total carti
                </div>
                <div className="mt-1 text-3xl font-semibold text-foreground">
                  {stats?.totalProducts ?? 0} cărți
                </div>
                {stats?.lowStockProducts &&
                  stats?.lowStockProducts?.length > 0 && (
                    <Link
                      to={`/products/${stats?.lowStockProducts[0]?.id}`}
                      className="text-sm text-primary font-medium mt-1"
                    >
                      Cel mai mic stoc: {stats?.lowStockProducts?.length ?? 0}
                    </Link>
                  )}
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Out of Stock Warning */}
      {stats?.lowStockProducts?.some((product) => product.stock === 0) && (
        <div className="mt-8">
          <div className="bg-red-500/10 border border-border rounded-lg p-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <h2 className="text-lg font-semibold text-red-500">
                Atenție - Stoc Epuizat
              </h2>
            </div>
            <div className="mt-4 space-y-3">
              {stats.lowStockProducts
                .filter((product) => product.stock === 0)
                .map((product) => (
                  <div
                    key={product.id}
                    className="flex items-center justify-between bg-background p-3 rounded-md shadow-sm"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground">
                        {product.name}
                      </span>
                      <span className="text-sm text-red-600 font-medium">
                        Stoc: 0
                      </span>
                    </div>
                    <Link
                      to={`/products/${product.id}`}
                      className="text-red-600 hover:text-red-800 font-medium text-sm flex items-center gap-1"
                    >
                      <Eye className="w-4 h-4" />
                      Verifică Produsul
                    </Link>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Recent orders table */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2 mb-4">
          <Package2 className="w-5 h-5 text-primary" />
          Ultimele Comenzi
        </h2>
        <Card className="">
          {false ? (
            <Card className="p-5 text-center text-foreground ">
              Nu există comenzi înregistrate
            </Card>
          ) : (
            <div className="overflow-x-auto  rounded-lg">
              <table className="min-w-full divide-y bg-background divide-border">
                <thead className="bg-background border-b border-border">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Produse
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Acțiuni
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-background divide-y divide-gray-200">
                  {stats?.recentOrders?.map((order) => (
                    <tr
                      key={order.id}
                      className="hover:bg-gray-50 transition-colors duration-200"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className="font-medium text-foreground">
                          #{order.id}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className="font-medium text-foreground">
                          {order.items.slice(0, 3).map((item, i) => (
                            <p className="text-xs" key={i}>
                              {item.bookName}
                            </p>
                          ))}
                          {order.items.length > 3 && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger className="text-xs text-blue-600 hover:text-blue-800">
                                  +{order.items.length - 3} more items
                                </TooltipTrigger>
                                <TooltipContent>
                                  <div className="max-w-xs">
                                    {order.items.slice(3).map((item, i) => (
                                      <p key={i} className="text-sm">
                                        {item.bookName}
                                      </p>
                                    ))}
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {new Date(order.createdAt).toLocaleDateString("ro-RO")}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div>
                          <div className="font-medium text-foreground">
                            {order.customerName}
                          </div>
                          <div className="text-muted-foreground text-xs">
                            {order.customerEmail}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className="font-medium text-foreground">
                          {formatPrice(order.totalPrice)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-3 py-1.5 inline-flex text-xs leading-5 font-semibold rounded-full shadow-sm ring-1 ring-inset ${getStatusColor(
                            order.status
                          )}`}
                        >
                          {getStatusLabel(order.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-2 hover:bg-gray-100"
                            onClick={() => handlePrintAwb(order.id)}
                            disabled={
                              // !order.awbNumber ||
                              order.metadata?.awbPrinted ||
                              order.status == "Anulată"
                            }
                            title={
                              !order.awbNumber
                                ? "AWB indisponibil"
                                : order.status === "Anulată"
                                ? "Comanda este anulată"
                                : order.metadata?.awbPrinted
                                ? "AWB deja printat"
                                : "Printează AWB"
                            }
                          >
                            <Printer className="w-4 h-4" />
                            {order.metadata?.awbPrinted
                              ? "Printat"
                              : "Printează AWB"}
                          </Button>
                          <Link
                            to={`/orders/${order.id}/`}
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-900 font-medium"
                          >
                            <Eye className="w-4 h-4" />
                            Detalii
                            <ChevronRight className="w-4 h-4" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <p className="text-sm text-blue-600 flex items-center gap-2">
        <Info />
        <span className="ml-2">
          Acesta pagina dashboard se actualizeaza la 5 minute pentru a oferi o
          performanta mai buna a siteului.
        </span>
      </p>
    </div>
  );
};
